// Sample data used only when REACT_APP_AWS_LISTS_API_URL is unset, so the page can be
// seen and clicked through before the Lambda exists. Delete this file and the demoMode
// branches in ListsContext once the API is connected.
import { ListItem, ListMeta, ListsPayload } from "./types";

const DEMO_STORAGE_KEY = "ListsDemoPayload";

// Fixed timestamp so the seed is stable across reloads.
const SEEDED_AT = "2026-09-01T12:00:00.000Z";

function meta(listId: string, name: string, sortOrder: number): ListMeta {
  return {
    ListId: listId,
    Name: name,
    SortOrder: sortOrder,
    CreatedAt: SEEDED_AT,
    UpdatedAt: SEEDED_AT,
  };
}

function item(
  listId: string,
  itemId: string,
  text: string,
  sortOrder: number,
  extras: Partial<Pick<ListItem, "Checked" | "Quantity" | "Note">> = {}
): ListItem {
  return {
    ListId: listId,
    ItemId: itemId,
    Text: text,
    Checked: extras.Checked ?? false,
    Quantity: extras.Quantity ?? "",
    Note: extras.Note ?? "",
    SortOrder: sortOrder,
    CreatedAt: SEEDED_AT,
    UpdatedAt: SEEDED_AT,
  };
}

const GROCERIES = "demo-list-groceries";
const GARAGE = "demo-list-garage";
const CAMPING = "demo-list-camping";

export function seedDemoPayload(): ListsPayload {
  return {
    lists: [
      meta(GROCERIES, "Groceries", 1000),
      meta(GARAGE, "Garage To-Do", 2000),
      meta(CAMPING, "Camping Trip", 3000),
    ],
    items: [
      item(GROCERIES, "demo-item-1", "Coffee beans", 1000, {
        Quantity: "2 bags",
        Note: "The dark roast from the corner place",
      }),
      item(GROCERIES, "demo-item-2", "Olive oil", 2000, { Quantity: "1 L" }),
      item(GROCERIES, "demo-item-3", "Paper towels", 3000, {
        Quantity: "a couple",
      }),
      item(GROCERIES, "demo-item-4", "Dish soap", 4000, { Checked: true }),
      item(GROCERIES, "demo-item-5", "Eggs", 5000, {
        Quantity: "18",
        Checked: true,
      }),

      item(GARAGE, "demo-item-6", "Replace the furnace filter", 1000, {
        Note: "20x25x1 — there are spares on the shelf",
      }),
      item(GARAGE, "demo-item-7", "Sharpen the mower blade", 2000),
      item(GARAGE, "demo-item-8", "Label the paint cans", 3000, {
        Quantity: "6",
        Note: "Room name and the date on each lid",
      }),
      item(GARAGE, "demo-item-9", "Hang the bike rack", 4000, {
        Checked: true,
      }),

      item(CAMPING, "demo-item-10", "Sleeping bags", 1000, { Quantity: "4" }),
      item(CAMPING, "demo-item-11", "Propane canisters", 2000, {
        Quantity: "3",
        Note: "Check what is left in the green tote first",
      }),
      item(CAMPING, "demo-item-12", "Headlamp batteries", 3000, {
        Quantity: "AAA x8",
      }),
      item(CAMPING, "demo-item-13", "First aid kit", 4000),
    ],
  };
}

// Demo edits persist to localStorage so the page survives a refresh.
export function loadDemoPayload(): ListsPayload {
  try {
    const stored = window.localStorage.getItem(DEMO_STORAGE_KEY);
    if (stored != null) {
      const parsed = JSON.parse(stored) as ListsPayload;
      if (Array.isArray(parsed?.lists) && Array.isArray(parsed?.items)) {
        return parsed;
      }
    }
  } catch {
    // Fall through to a fresh seed.
  }
  return seedDemoPayload();
}

export function saveDemoPayload(payload: ListsPayload) {
  try {
    window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage unavailable — demo edits just won't survive a refresh.
  }
}

export function clearDemoPayload() {
  try {
    window.localStorage.removeItem(DEMO_STORAGE_KEY);
  } catch {
    // Nothing to do.
  }
}
